/**
 * Storage Version Management
 * Handles version tracking and automatic cache clearing for storage schema changes
 */

const STORAGE_VERSION_KEY = 'lecturo_storage_version';
const CURRENT_STORAGE_VERSION = '1.1.0'; // Incremented for role context system

interface StorageVersionInfo {
  version: string;
  lastUpdated: string;
  features: string[];
}

/**
 * Get current storage version information
 */
export const getCurrentStorageVersion = (): StorageVersionInfo | null => {
  try {
    const stored = localStorage.getItem(STORAGE_VERSION_KEY);
    if (!stored) return null;

    return JSON.parse(stored);
  } catch (error) {
    console.warn('Failed to parse storage version info:', error);
    return null;
  }
};

/**
 * Set storage version information
 */
export const setStorageVersion = (features: string[] = []): void => {
  try {
    const versionInfo: StorageVersionInfo = {
      version: CURRENT_STORAGE_VERSION,
      lastUpdated: new Date().toISOString(),
      features,
    };

    localStorage.setItem(STORAGE_VERSION_KEY, JSON.stringify(versionInfo));
  } catch (error) {
    console.error('Failed to set storage version:', error);
  }
};

/**
 * Check if storage version is compatible with current app version
 */
export const isStorageVersionCompatible = (): boolean => {
  const currentVersionInfo = getCurrentStorageVersion();

  if (!currentVersionInfo) {
    return false;
  }

  return currentVersionInfo.version === CURRENT_STORAGE_VERSION;
};

/**
 * Clear all storage data (localStorage + sessionStorage)
 */
export const clearAllStorageData = (): void => {
  try {
    // Store keys to clear
    const localStorageKeys = ['auth-store', 'context-storage', 'lecturo_current_role_context'];
    const sessionStorageKeys = ['lecturo_current_role_context'];

    // Clear specific localStorage keys
    localStorageKeys.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });

    // Clear specific sessionStorage keys
    sessionStorageKeys.forEach((key) => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('❌ Failed to clear storage data:', error);
  }
};

/**
 * Perform storage migration if needed
 */
export const migrateStorageIfNeeded = (): boolean => {
  const isCompatible = isStorageVersionCompatible();

  if (!isCompatible) {
    // Clear incompatible storage data
    clearAllStorageData();

    // Set new version
    setStorageVersion(['auth-store', 'context-storage', 'role-context']);

    return true; // Migration performed
  }

  return false; // No migration needed
};

/**
 * Debug function to force storage reset (development only)
 */
export const debugForceStorageReset = (): void => {
  if (import.meta.env.DEV) {
    clearAllStorageData();
    localStorage.removeItem(STORAGE_VERSION_KEY);
  }
};

/**
 * Initialize storage version tracking
 */
export const initializeStorageVersion = (): boolean => {
  const migrationPerformed = migrateStorageIfNeeded();

  if (!migrationPerformed) {
    // Ensure version is set even if compatible
    setStorageVersion(['auth-store', 'context-storage', 'role-context']);
  }

  return migrationPerformed;
};

// Expose debug functions in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).debugForceStorageReset = debugForceStorageReset;
  (window as any).getCurrentStorageVersion = getCurrentStorageVersion;
  (window as any).migrateStorageIfNeeded = migrateStorageIfNeeded;
}
